import { DataTable, DataTableSortOrderType } from 'primereact/datatable';
import { Dispatch, RefObject, SetStateAction, useRef, useState } from 'react';

import { TableProps } from '../features/DataTable/Table/Table';
import { getSessionStorageItem } from '../utils/helpers/storage';
import { userPrefixedString } from '../utils/helpers/user';

export type TableStateReturn<T> = {
  tableRef: RefObject<DataTable>;
  sortField: TableProps['sortField'];
  setSortField: TableProps['setSortField'];
  defaultSortField: string;
  sortOrder: TableProps['sortOrder'];
  setSortOrder: TableProps['setSortOrder'];
  selection: T | null;
  setSelection: Dispatch<SetStateAction<T | null>>;
  selectionMultiple: T[];
  setSelectionMultiple: Dispatch<SetStateAction<T[]>>;
  page: TableProps['rows'];
  setPage: TableProps['setPage'];
  limit: number;
  setLimit: TableProps['setLimit'];
};

type TableStorage<T> = {
  columnWidths: string;
  first: number;
  rows: number;
  selection: T | T[];
  sortField: keyof T;
  sortOrder: number;
} | null;

function getInitialLimit<T>(storageKey: string) {
  return (
    getSessionStorageItem<TableStorage<T>>(userPrefixedString(storageKey))
      ?.rows ?? 30
  );
}

function useTableState<T extends object>(
  storageKey: string,
  defaultSortField: string = 'id'
): TableStateReturn<T> {
  const tableRef = useRef<DataTable>(null);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(getInitialLimit(storageKey));
  const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>(1);
  const [sortField, setSortField] = useState<string>(defaultSortField);
  const [selection, setSelection] = useState<T | null>(null);
  const [selectionMultiple, setSelectionMultiple] = useState<T[]>([]);

  return {
    tableRef,
    sortField,
    setSortField,
    defaultSortField,
    sortOrder: sortOrder ?? null,
    setSortOrder,
    selection,
    setSelection,
    selectionMultiple,
    setSelectionMultiple,
    page,
    setPage,
    limit,
    setLimit,
  };
}

export default useTableState;
